import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { List, ListItem as MuiListItem, ListItemText, ListItemIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import { navItemsForUserType1, navItemsForUserType2 } from "./constants";
import { TEST_CASE_GENERATION_URL, API_BASE_PATH } from "../../Config/Config";
import apiFetch from "../../utils/apiFetch";
import { AutoFixHighOutlined } from "@mui/icons-material";

const StyledListItem = styled(MuiListItem)(({ theme, active }) => ({
  color: "#8C9EB8",
  columnGap: "15px",
  marginBottom: "25px",
  "& .MuiTypography-root": {
    fontSize: "14px",
  },
  "& .MuiListItemText-root": {
    fontSize: "14px",
  },
  "&:hover": {
    backgroundColor: "#FF5336",
    borderRadius: "8px",
    color: "#242f3f",
    "& .MuiListItemIcon-root": {
      color: "#ffffff",
    },
    "& .MuiListItemText-root": {
      color: "#ffffff",
    },
  },
  "& .MuiListItemIcon-root": {
    color: "#8C9EB8",
    minWidth: "unset",
  },
  ...(active && {
    backgroundColor: "#FF5336",
    color: "#ffffff",
    borderRadius: "8px",
    "& .MuiListItemIcon-root": {
      color: "#ffffff",
      minWidth: "unset",
    },
    "& .MuiListItemText-root": {
      color: "#ffffff",
    },
  }),
}));

const Sidebar = () => {
  const location = useLocation();

  let navItems = navItemsForUserType1;
  let userType = 1;

  try {
    const userDetails = localStorage.getItem("userDetail");
    if (userDetails) {
      const user = JSON.parse(userDetails);
      userType = user?.usertype || 1;

      // set navigation items based on user type
      if (userType === 2) {
        navItems = navItemsForUserType2;
      } else {
        navItems = navItemsForUserType1;
      }
    }
  } catch (error) {
    console.error("Failed to parse user details from localStorage", error);
  }

  const handleTestCaseGeneration = async () => {
    const generatorWindow = window.open("", "_blank");

    try {
      const user = JSON.parse(localStorage.getItem("userDetail"));

      let fileName = null;
      try {
        fileName = new URL(user.company?.brandLogo).searchParams.get("fileName");
      } catch {
        // No usable brand logo — the generator falls back to the default.
      }

      const response = await apiFetch(`${API_BASE_PATH}/KeyLogging/handoff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          companyId: String(user.companyid ?? ""),
          brandLogo: fileName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Handoff request failed: ${response.status}`);
      }

      const { code } = await response.json();
      if (!code) {
        throw new Error("Handoff response contained no code.");
      }

      const url = `${TEST_CASE_GENERATION_URL}?handoff=${encodeURIComponent(code)}`;
      if (generatorWindow) {
        generatorWindow.location = url;
      } else {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Failed to open test case generation", error);
      generatorWindow?.close();
    }
  };

  return (
    <>
      <List sx={{ flex: "1" }}>
        {navItems.map((item) => {
          if (item.external) {
            return (
              <StyledListItem
                key={item.text}
                onClick={() => window.open(item.path, "_blank", "noopener,noreferrer")}
                sx={{ cursor: "pointer" }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </StyledListItem>
            );
          }

          const isActive =
            location.pathname === item.path ||
            (location.pathname === "/new" && item.text === "Dashboard") ||
            location.pathname.startsWith(`${item.path}/`) ||
            (item.path === "/new/test-case-list" &&
              (location.pathname === "/new/test-studio" ||
                location.pathname.startsWith("/new/test-studio/")));
          return (
            <StyledListItem
              key={item.text}
              component={NavLink}
              to={item.path}
              active={isActive ? 1 : 0}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </StyledListItem>
          );
        })}

        {/* GenAI Testing - for usertype 1 and 2 */}
        {(userType === 1 || userType === 2) && (
          <StyledListItem onClick={handleTestCaseGeneration} sx={{ cursor: "pointer" }}>
            <ListItemIcon>
              <AutoFixHighOutlined />
            </ListItemIcon>
            <ListItemText primary={"GenAI Testing"} />
          </StyledListItem>
        )}
      </List>

      {/* Settings - for usertype 1 and 2 */}
      {(userType === 1 || userType === 2) && (
        <List>
          <StyledListItem
            sx={{ marginBottom: 0 }}
            component={NavLink}
            to={"/new/settings"}
            active={location.pathname === "/new/settings" ? 1 : 0}
          >
            <ListItemIcon>
              <SettingsOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={"Settings"} />
          </StyledListItem>
        </List>
      )}
    </>
  );
};

export default Sidebar;
